import { KeysMatching } from 'framework/typescript-integration';
import { newGuid } from 'tools/guid';
import { generateUniqueName } from './generateUniqueName';

export default class ArrayHelpers {
	static generateUniqueName<T extends { name: string }>(items: T[], name: string) {
		return generateUniqueName(items, 'name', name);
	}

	static generateId<T extends { id: string }>(array: T[], subArrayAccessor?: (element: T) => T[]) {
		array.forEach((entry) => {
			if (!entry.id) {
				entry.id = newGuid();
				if (subArrayAccessor) {
					ArrayHelpers.generateId(subArrayAccessor(entry));
				}
			}
		});

		return array;
	}

	static cleanId<T extends { id: string }>(array: T[]) {
		array.forEach((entry) => {
			delete entry.id;
		});

		return array;
	}

	static remove<T, K extends keyof T>(array: T[], element: T, realId: K = null) {
		let index;

		if (realId && element[realId] != null) {
			index = array.findIndex((e) => e[realId] == element[realId]);
		} else {
			index = array.indexOf(element);
		}
		if (index != -1) {
			array.splice(index, 1);
		}
		return array;
	}

	static removeByPredicate<T>(array: T[], filter: (element: T) => boolean) {
		let index = -1;
		do {
			index = array.findIndex(filter);
			if (index == -1) break;
			array.splice(index, 1);
		} while (index != -1);
	}

	static addOrReplace<T, K extends KeysMatching<T, string>>(array: T[], entry: T, idFieldName: K) {
		if (entry[idFieldName] == null) {
			array.push(entry);
			entry[idFieldName] = newGuid() as T[K];
		} else {
			const index = array.findIndex((e) => e[idFieldName] == entry[idFieldName]);
			if (index == -1) {
				array.push(entry);
			} else {
				array.splice(index, 1, entry);
			}
		}
	}

	static objectToArray<T extends object, K extends keyof T>(object: object, propertyField: K) {
		return Object.entries(object).map(([key, value]) => {
			return {
				[propertyField]: key,
				...value,
			};
		});
	}
}

export function getLastEntry<T>(array: T[]): T {
	if (array.length == 0) return null;

	return array[array.length - 1];
}

export function getFirstEntry<T>(array: T[]): T {
	if (array.length == 0) return null;

	return array[0];
}

export function takeWhile<T>(fn: (element: T) => boolean, array: T[], start: number = 0): T[] {
	const result = [];

	for (let i = start; i < array.length; i++) {
		if (fn(array[i])) result.push(array[i]);
		else break;
	}

	return result;
}

export function insertAfter<T>(arr: T[], moved: T, after: T = null) {
	if (moved === after) {
		return arr;
	}
	const copy = arr.slice();
	const movedIndex = arr.indexOf(moved);
	const afterIndex = arr.indexOf(after);
	copy.splice(movedIndex, 1);
	if (movedIndex > afterIndex) {
		copy.splice(afterIndex + 1, 0, moved);
	} else {
		copy.splice(afterIndex, 0, moved);
	}
	return copy;
}

export function deleteByCriteria<T>(array: T[], criteria: (item: T) => boolean) {
	if (!array || !criteria) return false;

	const itemIndex = array.findIndex(criteria);
	if (itemIndex == -1) return false;

	array.splice(itemIndex, 1);

	return true;
}

export function updateNullFields<T extends object>(target: T, source: T) {
	(Object.keys(source) as Array<keyof T>).forEach((x) => {
		if (target[x] === null || target[x] === undefined) {
			target[x] = source[x];
		}
	});
}

export function updateNotNullFields<T extends object>(target: T, source: T) {
	(Object.keys(source) as Array<keyof T>).forEach((x) => {
		if (source[x] !== null && source[x] !== undefined) {
			target[x] = source[x];
		}
	});
}
